import React, {useCallback, useEffect, useState} from 'react';

import {addQueryToPath} from '../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useUnmountCancelToken} from '../../../../../../srcCommon/helpers/hookHelpers';
import {FolderBreadcrumb} from '../../../../../components/folderBreadcrumbs/folderBreadcrumbs';
import {CancelToken} from '../../../../../helpers/asyncCancelHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {CompanyModel} from '../../../../../models/api/companyModel';
import {HighlightTypesEnum} from '../../../../../models/api/highlightBaseModel';
import {RuleBundleModel} from '../../../../../models/api/ruleBundleModel';
import {ApiTag, ApiTags, TagTypesEnum} from '../../../../../models/api/tagModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {AppThunkDispatch} from '../../../../../store/appThunk';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {createTagAction} from '../../../../../store/data/tags/saveTagActions';
import {CustomFields} from '../../../../editors/commons/customFieldsModel';
import {RuleIntegration} from '../../../ruleIntegrationModel';
import {RuleEditorSkeleton} from '../../ruleEditorSkeleton';
import {createRecipeTag} from '../helpers/recipesHelpers';
import {SlaEditor} from './slaEditor';
import {SlaTagsModels} from './state/slaEditorState';

/*
 * Props.
 */

interface SlaEditorWrapperProps {
  namespace: string;
  ruleIntegration: RuleIntegration;
  customFields: CustomFields;
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  ruleBundle?: RuleBundleModel;
  isEditingRule: boolean;

  navigateToRules: Handler;
}

/*
 * Component.
 */

export const SlaEditorWrapper: React.FC<SlaEditorWrapperProps> = (props) => {
  const {namespace, isEditingRule, breadcrumbs} = props;

  const slaTags = useSlaTags(namespace);

  if (!slaTags) {
    return (
      <RuleEditorSkeleton
        breadcrumbs={breadcrumbs}
        namespace={namespace}
        isEditing={isEditingRule}
        isActive
      />
    );
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <SlaEditor {...props} slaTags={slaTags} />;
};

/*
 * Helpers.
 */

function useSlaTags(namespace: string) {
  const company = useAppSelector(getCompany);
  const cancelToken = useUnmountCancelToken();
  const dispatch = useAppDispatch();
  const [slaTags, setSlaTags] = useState<SlaTagsModels>();

  const fetchTags = useCallback(() => {
    fetchSlaTags(company, namespace, cancelToken, dispatch).then((fetchedSlaTags) => {
      setSlaTags(fetchedSlaTags);
    });
  }, [cancelToken, company, dispatch, namespace]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return slaTags;
}

async function fetchSlaTags(
  company: CompanyModel,
  namespace: string,
  cancelToken: CancelToken,
  dispatch: AppThunkDispatch,
): Promise<SlaTagsModels> {
  const [applies, {breach, warning}] = await Promise.all([
    fetchOrCreateSlaAppliesTag(company, namespace, cancelToken, dispatch),
    fetchOrCreateSlaTags(company, namespace, cancelToken, dispatch),
  ]);

  return {
    applies,
    breach,
    warning,
  };
}

async function fetchOrCreateSlaAppliesTag(
  company: CompanyModel,
  namespace: string,
  cancelToken: CancelToken,
  dispatch: AppThunkDispatch,
) {
  try {
    return await fetchApiAsync(ApiTag, `${company.url}/namespaces/${namespace}/tags/!sla_applies`, {
      cancelToken,
    });
  } catch (err) {
    if (err?.status !== 404) {
      throw err;
    }
  }

  return dispatch(
    createTagAction({
      name: 'SLA Applies',
      namespace,
      type: TagTypesEnum.SLA_RULE,
      isVisibleInAllInboxes: true,
      isSlaAppliesTag: true,
      inboxIds: [],
      showInCells: false,
      highlightType: HighlightTypesEnum.NONE,
      parentTags: [],
      showInWorkspaces: false,
      teammatesToPin: [],
    }),
  );
}

async function fetchOrCreateSlaTags(
  company: CompanyModel,
  namespace: string,
  cancelToken: CancelToken,
  dispatch: AppThunkDispatch,
) {
  const url = `${company.url}/search_tag2/sla`;

  const urlWithParams = addQueryToPath(url, {
    inbox_ids: null,
    namespaces: namespace,
    offset: 0,
    limit: 50,
    admin_override: true,
    behaviors: 'can_rule',
    tag_status: 'all',
  });

  const tags = await fetchApiAsync(ApiTags, urlWithParams, {cancelToken});

  let slaBreachTag = tags.find((tag) => tag.alias.toLocaleLowerCase() === 'sla_breach');
  let slaWarningTag = tags.find((tag) => tag.alias.toLocaleLowerCase() === 'sla_warning');

  if (!slaBreachTag) {
    slaBreachTag = await createSlaBreachTag(namespace, dispatch);
  }

  if (!slaWarningTag) {
    slaWarningTag = await createSlaWarningTag(namespace, dispatch);
  }

  return {breach: slaBreachTag, warning: slaWarningTag};
}

async function createSlaBreachTag(namespace: string, dispatch: AppThunkDispatch) {
  return createRecipeTag('SLA Breach', '🔥', namespace, dispatch);
}

async function createSlaWarningTag(namespace: string, dispatch: AppThunkDispatch) {
  return createRecipeTag('SLA Warning', '⚠️', namespace, dispatch);
}
